export interface Loan {
  id: string;
  borrower: {
    name: string;
    company: string;
    email: string;
    phone: string;
  };
  property: {
    address: string;
    type: string;
    value: number;
  };
  terms: {
    amount: number;
    interestRate: number;
    term: number; // in months
    startDate: string;
    maturityDate: string;
    paymentFrequency: string;
    paymentAmount: number;
  };
  status: string;
  fees: {
    originationFee: number;
    processingFee: number;
    latePaymentFee: number;
    date: string;
  }[];
  payments: {
    id: string;
    date: string;
    amount: number;
    type: string;
    status: string;
  }[];
}

export const mockLoans: Loan[] = [
  {
    id: "L1001",
    borrower: {
      name: "John Smith",
      company: "Smith Real Estate LLC",
      email: "john@smithrealestate.com",
      phone: "(555) 123-4567"
    },
    property: {
      address: "123 Main St, Austin, TX 78701",
      type: "Commercial Office",
      value: 2500000
    },
    terms: {
      amount: 1800000,
      interestRate: 6.5,
      term: 24,
      startDate: "2024-01-15",
      maturityDate: "2026-01-15",
      paymentFrequency: "Monthly",
      paymentAmount: 82500
    },
    status: "Active",
    fees: [
      {
        originationFee: 36000,
        processingFee: 2500,
        latePaymentFee: 0,
        date: "2024-01-15"
      }
    ],
    payments: [
      {
        id: "P1001",
        date: "2024-02-15",
        amount: 82500,
        type: "Regular Payment",
        status: "Completed"
      },
      {
        id: "P1002",
        date: "2024-03-15",
        amount: 82500,
        type: "Regular Payment",
        status: "Completed"
      }
    ]
  },
  {
    id: "L1002",
    borrower: {
      name: "Sarah Johnson",
      company: "Johnson Development Corp",
      email: "sarah@johnsondevelopment.com",
      phone: "(555) 987-6543"
    },
    property: {
      address: "456 Oak Ave, Houston, TX 77002",
      type: "Multi-family Residential",
      value: 3800000
    },
    terms: {
      amount: 2850000,
      interestRate: 7.2,
      term: 36,
      startDate: "2023-11-01",
      maturityDate: "2026-11-01",
      paymentFrequency: "Monthly",
      paymentAmount: 95000
    },
    status: "Active",
    fees: [
      {
        originationFee: 57000,
        processingFee: 3000,
        latePaymentFee: 2500,
        date: "2023-11-01"
      }
    ],
    payments: [
      {
        id: "P2001",
        date: "2023-12-01",
        amount: 95000,
        type: "Regular Payment",
        status: "Completed"
      },
      {
        id: "P2002",
        date: "2024-01-01",
        amount: 95000,
        type: "Regular Payment",
        status: "Completed"
      },
      {
        id: "P2003",
        date: "2024-02-01",
        amount: 95000,
        type: "Regular Payment",
        status: "Completed"
      }
    ]
  },
  {
    id: "L1003",
    borrower: {
      name: "Michael Chen",
      company: "Chen Properties Inc",
      email: "michael@chenproperties.com",
      phone: "(555) 246-8135"
    },
    property: {
      address: "789 Pine St, Dallas, TX 75201",
      type: "Retail",
      value: 1750000
    },
    terms: {
      amount: 1225000,
      interestRate: 6.8,
      term: 18,
      startDate: "2024-02-01",
      maturityDate: "2025-08-01",
      paymentFrequency: "Monthly",
      paymentAmount: 73500
    },
    status: "Active",
    fees: [
      {
        originationFee: 24500,
        processingFee: 2000,
        latePaymentFee: 0,
        date: "2024-02-01"
      }
    ],
    payments: [
      {
        id: "P3001",
        date: "2024-03-01",
        amount: 73500,
        type: "Regular Payment",
        status: "Completed"
      }
    ]
  }
];

// Helper function to calculate total fees for a date range
export const calculateTotalFees = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return mockLoans.reduce((total, loan) => {
    return total + loan.fees.reduce((loanTotal, fee) => {
      const feeDate = new Date(fee.date);
      if (feeDate >= start && feeDate <= end) {
        return loanTotal + fee.originationFee + fee.processingFee + fee.latePaymentFee;
      }
      return loanTotal;
    }, 0);
  }, 0);
};

// Helper function to get loan statistics
export const getLoanStatistics = () => {
  const totalActiveLoans = mockLoans.filter(loan => loan.status === "Active").length;
  const totalLoanAmount = mockLoans.reduce((total, loan) => total + loan.terms.amount, 0);
  const averageInterestRate = mockLoans.reduce((total, loan) => total + loan.terms.interestRate, 0) / mockLoans.length;
  
  return {
    totalActiveLoans,
    totalLoanAmount,
    averageInterestRate
  };
}; 